import { cn } from "@thilke/ui/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";

export interface LancerActionState {
  id: string;
  label: string;
  active?: boolean;
  disabled?: boolean;
  count?: number;
}

const lancerActionButtonVariants = cva(
  "relative inline-flex items-center justify-center gap-1.5 whitespace-nowrap border px-2 py-1 text-xs font-mono font-semibold uppercase tracking-wider transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      active: {
        true: "border-primary/70 bg-primary text-primary-foreground shadow-[inset_0_1px_2px_rgba(0,0,0,0.2)]",
        false:
          "border-border/70 bg-card text-muted-foreground shadow-sm hover:border-primary/40 hover:text-foreground",
      },
      shape: {
        default: "rounded-[3px]",
        pill: "rounded-full px-3",
      },
    },
    defaultVariants: {
      active: false,
      shape: "default",
    },
  },
);

export interface LancerActionButtonProps
  extends Omit<React.ComponentProps<"button">, "children" | "onToggle">,
    VariantProps<typeof lancerActionButtonVariants> {
  action: LancerActionState;
  onToggle?: (id: string) => void;
}

function LancerActionButton({
  action,
  active,
  shape = "default",
  onToggle,
  className,
  ...props
}: LancerActionButtonProps) {
  return (
    <button
      type="button"
      data-slot="lancer-action-button"
      data-active={action.active ? "true" : "false"}
      data-id={action.id}
      disabled={action.disabled}
      onClick={() => onToggle?.(action.id)}
      className={cn(
        lancerActionButtonVariants({ active: action.active, shape }),
        className,
      )}
      {...props}
    >
      {shape === "default" && (
        <span className="absolute left-0.5 top-0.5 size-0.5 rounded-full bg-current opacity-40" />
      )}
      <span>{action.label}</span>
      {typeof action.count === "number" ? (
        <span className="ml-0.5 min-w-[1rem] rounded-sm bg-background/20 px-1 text-center text-[10px]">
          {action.count}
        </span>
      ) : null}
    </button>
  );
}

export interface LancerActionTrackerProps
  extends Omit<React.ComponentProps<"div">, "children" | "onToggle"> {
  actions: LancerActionState[];
  onToggle?: (id: string) => void;
  label?: React.ReactNode;
}

function LancerActionTracker({
  actions,
  onToggle,
  label,
  className,
  ...props
}: LancerActionTrackerProps) {
  return (
    <div
      data-slot="lancer-action-tracker"
      className={cn("grid gap-1.5", className)}
      {...props}
    >
      {label ? (
        <span
          data-slot="lancer-action-tracker-label"
          className="font-mono text-[10px] font-semibold uppercase tracking-wider text-muted-foreground"
        >
          {label}
        </span>
      ) : null}
      <div className="flex flex-wrap gap-1">
        {actions.map((action) => (
          <LancerActionButton
            key={action.id}
            action={action}
            onToggle={onToggle}
          />
        ))}
      </div>
    </div>
  );
}

export { LancerActionButton, LancerActionTracker, lancerActionButtonVariants };
